import { useMemo, useState } from 'react';
import {
  useClients,
  useCurrentUserProfile,
  useLatestClientPhotoUrls,
  useTodaysVisits,
  type ClientRow,
  type VisitRow,
} from '@busy/api';
import { getExperienceLevel } from '@busy/core';
import { Avatar, ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import { renderEodImage } from '../../lib/eodImage';

// End-of-day wrap-up. Mirrors the demo's openEodReport but trimmed for v1's
// Busy-mode-only scope: no integrated appointments, no no-show tracking, no
// tomorrow preview, no booking-platform sync checklist. What we do have:
//   - Today's visits (cuts count, chair time, revenue, avg ticket)
//   - Add-on attach inference from the cut_type string (heuristic: contains "+ ")
//   - Coaching insight picked from a candidate list weighted by signal
//   - Tone-aware copy via getExperienceLevel(years_cutting)
//
// Reachable from the chair view's "Generate wrap-up" CTA. Closes back to chair.
export function EodWrapupModal({ onClose }: { onClose: () => void }) {
  const visits = useTodaysVisits();
  const clients = useClients();
  const profile = useCurrentUserProfile();
  const photoUrls = useLatestClientPhotoUrls();
  const t = useT();
  const language = useUI((s) => s.language);
  const toast = useToast();
  // All three sections start collapsed for a calmer wrap-up screen —
  // the barber taps any header to expand the details they want to see.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    cuts: false,
    money: false,
    addons: false,
  });

  const clientById = useMemo(() => {
    const m = new Map<string, ClientRow>();
    (clients.data ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [clients.data]);

  const cutFallback = t('eod.cutFallback');
  const agg = useMemo(
    () => aggregate(visits.data ?? [], language, cutFallback),
    [visits.data, language, cutFallback],
  );
  const level = getExperienceLevel(profile.data?.years_cutting ?? null);
  const insight = useMemo(() => {
    const ins = pickInsight(agg, level);
    return t(ins.key, ins.vars);
  }, [agg, level, t]);

  const dateStr = new Date().toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const cutLabelFor = (n: number) => (n === 1 ? t('eod.cut_one') : t('eod.cut_other'));
  const chairTimeStr = formatChairTime(agg.totals.chairTimeMin, t);

  function toggle(key: string) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <ModalShell open onClose={onClose} size="lg" ariaLabel={t('eod.aria')}>
      {/* flex-col frame so the Share / Done footer stays pinned no matter
          how many cuts + add-on rollups the body has to render. */}
      <div className="flex flex-col max-h-[90dvh]">
      <ModalCloseButton onClose={onClose} />
      <div className="shrink-0 px-6 pt-7 pb-3">
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-500 inline-flex items-center gap-1.5 mb-1.5">
          <BarChartIcon className="w-3 h-3" />
          {t('eod.eyebrow')} · {dateStr}
        </div>
        <h2 className="font-display text-2xl">{t('eod.headline')}</h2>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3 min-h-0">
        {visits.loading && (
          <div className="space-y-2">
            <div className="h-16 rounded-xl bg-bg-alt animate-pulse" />
            <div className="h-16 rounded-xl bg-bg-alt animate-pulse" />
            <div className="h-16 rounded-xl bg-bg-alt animate-pulse" />
          </div>
        )}

        {visits.data && visits.data.length === 0 && (
          <div className="rounded-xl border border-dashed border-line p-8 text-center text-ink-500">
            <p className="text-sm font-semibold">{t('eod.empty.title')}</p>
            <p className="text-xs mt-1">{t('eod.empty.body')}</p>
          </div>
        )}

        {visits.data && visits.data.length > 0 && (
          <>
            <KpiCard
              expanded={expanded.cuts}
              onToggle={() => toggle('cuts')}
              headline={t('eod.headlineCuts', {
                cuts: agg.totals.cuts,
                cutLabel: cutLabelFor(agg.totals.cuts),
                chairTime: chairTimeStr,
              })}
              eyebrow={t('eod.kpi.cuts')}
            >
              <ul className="divide-y divide-line">
                {agg.cuts.map((c) => {
                  const linked = c.clientId ? clientById.get(c.clientId) : null;
                  const name = linked
                    ? `${linked.first_name} ${linked.last_name}`.trim() || t('today.unnamed')
                    : t('today.walkin');
                  return (
                    <li key={c.visitId} className="py-2 flex items-center gap-3 text-sm">
                      <span className="text-ink-500 tabular-nums w-16 shrink-0">{c.timeLabel}</span>
                      {linked && (
                        <Avatar
                          name={name}
                          size="sm"
                          photoUrl={c.clientId ? photoUrls.data?.[c.clientId] : undefined}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <strong className="truncate">{name}</strong>
                          {!linked && (
                            <span className="text-[10px] uppercase tracking-wide font-bold bg-bg-alt text-ink-500 px-1.5 py-0.5 rounded">
                              {t('eod.walkinPill')}
                            </span>
                          )}
                        </div>
                        {c.cutTypeName && (
                          <div className="text-xs text-ink-500 truncate">{c.cutTypeName}</div>
                        )}
                      </div>
                      <div className="text-right shrink-0 tabular-nums">
                        <div className="font-bold">${c.totalPrice}</div>
                        <div className="text-[11px] text-ink-500">{c.actualMin} min</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </KpiCard>

            <KpiCard
              expanded={expanded.money}
              onToggle={() => toggle('money')}
              headline={t('eod.earned', { rev: agg.totals.totalRevenue })}
              eyebrow={t('eod.kpi.revenue')}
            >
              <div className="space-y-1.5 text-sm">
                <Row label={t('eod.row.avgTicket')} value={`$${agg.totals.avgTicket}`} />
                <Row label={t('eod.row.fromWalkins')} value={`$${agg.totals.walkinRevenue}`} />
                <Row label={t('eod.row.fromRegulars')} value={`$${agg.totals.clientRevenue}`} />
                <div className="border-t border-line pt-1.5 mt-1.5">
                  <Row label={t('eod.row.total')} value={`$${agg.totals.totalRevenue}`} bold />
                </div>
              </div>
            </KpiCard>

            <KpiCard
              expanded={expanded.addons}
              onToggle={() => toggle('addons')}
              headline={
                agg.totals.cuts === 0
                  ? '—'
                  : t('eod.attachHeadline', {
                      count: agg.totals.attachCount,
                      total: agg.totals.cuts,
                      pct: Math.round(agg.totals.attachRate * 100),
                    })
              }
              eyebrow={t('eod.kpi.attach')}
            >
              {agg.totals.attachCount === 0 ? (
                <p className="text-sm text-ink-500">{t('eod.attach.empty')}</p>
              ) : (
                <div className="space-y-2">
                  <ul className="space-y-1.5 text-sm">
                    {agg.addonRollup.map((a) => (
                      <li key={a.name} className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className="inline-flex w-6 justify-center text-[11px] font-extrabold tracking-[0.05em] text-gold-500 bg-gold-500/10 rounded py-0.5 pr-1"
                            style={{ paddingLeft: 'calc(0.25rem + 0.05em)' }}
                          >
                            ×{a.count}
                          </span>
                          <span className="truncate">{a.name}</span>
                        </span>
                        <span className="font-bold tabular-nums text-ink-700">
                          {a.revenueDollars > 0 ? `$${a.revenueDollars}` : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {agg.addonRollup.some((a) => a.revenueDollars === 0) && (
                    <p className="text-[11px] text-ink-500 leading-snug">
                      {t('eod.attach.legacyNote')}
                    </p>
                  )}
                </div>
              )}
            </KpiCard>

            <div className="rounded-xl border border-gold-300/60 dark:border-gold-500/30 bg-gold-50/60 dark:bg-gold-500/10 p-4 mt-2">
              <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-[#93701c] dark:text-gold-300 inline-flex items-center gap-1.5 mb-2">
                <BoltIcon className="w-3 h-3" />
                {t('eod.forYou')}
              </div>
              <p className="text-sm leading-relaxed text-ink-900">{insight}</p>
            </div>
          </>
        )}
      </div>

      <div className="shrink-0 border-t border-line bg-bg-alt p-4 flex gap-2">
        <button
          type="button"
          onClick={() => handleShare(agg, language, t, toast)}
          disabled={!visits.data || visits.data.length === 0}
          className="flex-1 h-11 rounded-lg border-2 border-navy-900 dark:border-gold-500 text-navy-900 dark:text-gold-500 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <ShareIcon className="w-4 h-4" />
          {t('eod.share')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold"
        >
          {t('eod.done')}
        </button>
      </div>
      </div>
    </ModalShell>
  );
}

// Mirrors demo's _eodBuildTextSummary + eodShare. Web Share API on mobile (native
// share sheet), clipboard fallback on desktop / when share is denied.
async function handleShare(
  agg: Aggregate,
  language: 'en' | 'es',
  t: (key: string, vars?: Record<string, string | number>) => string,
  toast: (msg: string, kind?: 'success' | 'error' | 'info') => void,
) {
  const dateStr = new Date().toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const hours = Math.floor(agg.totals.chairTimeMin / 60);
  const mins = agg.totals.chairTimeMin % 60;
  const lines: string[] = [];
  lines.push(`${t('eod.share.title')} — ${dateStr}`);
  lines.push('');
  lines.push(
    t(agg.totals.cuts === 1 ? 'eod.share.sumLine_one' : 'eod.share.sumLine_other', {
      cuts: agg.totals.cuts,
      h: hours,
      m: mins,
    }),
  );
  lines.push(t('eod.share.revenue', { rev: agg.totals.totalRevenue }));
  if (agg.cuts.length > 0) {
    lines.push('');
    lines.push(t('eod.share.cutsHeader'));
    for (const c of agg.cuts) {
      lines.push(`  ${c.timeLabel} — ${c.cutTypeName} · $${c.totalPrice}`);
    }
  }
  const text = lines.join('\n');
  const title = t('eod.share.title');

  // Render the wrap-up to a PNG so iMessage / Instagram / Stories carry the
  // actual visual instead of plain text. canShare gates the file payload —
  // some browsers (Firefox, Safari < 16.4) decline files even when share()
  // is present.
  let file: File | null = null;
  try {
    const blob = await renderEodImage({
      dateLabel: dateStr,
      totals: agg.totals,
      cuts: agg.cuts.map((c) => ({
        timeLabel: c.timeLabel,
        cutTypeName: c.cutTypeName,
        totalPrice: c.totalPrice,
      })),
      labels: {
        titleEyebrow: t('eod.share.title'),
        headline: dateStr,
        cutsLabel: t('eod.share.cutsLabel'),
        revenueLabel: t('eod.share.revenueLabel'),
        chairLabel: t('eod.share.chairLabel'),
        cutsHeader: t('eod.share.cutsHeader'),
      },
    });
    file = new File([blob], `busy-eod-${new Date().toISOString().slice(0, 10)}.png`, {
      type: 'image/png',
    });
  } catch {
    file = null;
  }

  const nav = typeof navigator !== 'undefined' ? (navigator as Navigator) : null;
  if (nav?.share) {
    try {
      const payload: ShareData = { title, text };
      if (file && nav.canShare && nav.canShare({ files: [file] })) {
        payload.files = [file];
      }
      await nav.share(payload);
      return;
    } catch {
      // User cancelled or share failed — fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    toast(t('eod.share.copied'), 'success');
  } catch {
    toast(t('eod.share.copyFail'), 'error');
  }
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function KpiCard({
  expanded,
  onToggle,
  headline,
  eyebrow,
  children,
}: {
  expanded: boolean;
  onToggle: () => void;
  headline: string;
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border bg-surface overflow-hidden transition-colors ${
        expanded ? 'border-navy-900/30 dark:border-gold-500/40' : 'border-line'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-bg-alt/50"
      >
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
            {eyebrow}
          </div>
          <div className="font-display text-xl mt-0.5">{headline}</div>
        </div>
        <ChevronIcon
          className={`w-4 h-4 text-ink-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      {expanded && <div className="px-4 pb-4 border-t border-line pt-3">{children}</div>}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? 'font-bold' : 'text-ink-700'}>{label}</span>
      <span className={`tabular-nums ${bold ? 'font-bold text-lg' : 'font-semibold'}`}>{value}</span>
    </div>
  );
}

/* ============================================================================
 * Aggregation — pure function over today's visits
 * ============================================================================ */

type CutSummary = {
  visitId: string;
  timeLabel: string;
  clientId: string | null;
  clientName: string | null; // null = walk-in
  cutTypeName: string;
  basePrice: number;
  addonNames: string[];
  totalPrice: number;
  actualMin: number;
  isWalkin: boolean;
};

type AddonRollup = {
  name: string;
  count: number;
  revenueDollars: number;
};

type Aggregate = {
  cuts: CutSummary[];
  totals: {
    cuts: number;
    chairTimeMin: number;
    totalRevenue: number;
    walkinRevenue: number;
    clientRevenue: number;
    avgTicket: number;
    attachCount: number;
    attachRate: number; // 0-1
  };
  addonRollup: AddonRollup[]; // sorted by count desc, then revenue desc
};

function aggregate(
  visits: VisitRow[],
  language: 'en' | 'es',
  cutFallback: string,
): Aggregate {
  // Sort chronologically so the cuts list reads top-to-bottom in time order
  const sorted = [...visits].sort(
    (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
  );
  const cuts: CutSummary[] = [];
  let totalRevenue = 0;
  let walkinRevenue = 0;
  let clientRevenue = 0;
  let chairTimeMin = 0;
  let attachCount = 0;
  const timeLocale = language === 'es' ? 'es-US' : 'en-US';
  // Per-add-on rollup keyed by name. Counts repeats of the same add-on
  // across the day (e.g. 3 beard trims, 2 hot towels).
  const addonByName = new Map<string, AddonRollup>();

  for (const v of sorted) {
    const time = new Date(v.started_at).toLocaleTimeString(timeLocale, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const isWalkin = v.client_id == null;
    const priceDollars = Math.round((v.price_cents ?? 0) / 100);
    const minutes = v.duration_min ?? 0;
    const cutTypeName = v.cut_type ?? cutFallback;
    // Source of truth is the structured visits.addons jsonb (migration 0021).
    // Fall back to parsing the cut_type string for historical rows that
    // pre-date that column.
    const structuredAddons = Array.isArray(v.addons) ? v.addons : [];
    const addonNames =
      structuredAddons.length > 0 ? structuredAddons.map((a) => a.name) : parseAddons(cutTypeName);
    const hasAddons = addonNames.length > 0;
    if (hasAddons) attachCount++;
    // Build per-addon rollup. Structured rows give us proper revenue;
    // string-parsed fallback rows count toward the count but contribute
    // $0 in revenue (we don't know what the add-on cost).
    if (structuredAddons.length > 0) {
      for (const a of structuredAddons) {
        const cur = addonByName.get(a.name) ?? { name: a.name, count: 0, revenueDollars: 0 };
        cur.count += 1;
        cur.revenueDollars += Math.round((a.priceCents ?? 0) / 100);
        addonByName.set(a.name, cur);
      }
    } else {
      for (const name of addonNames) {
        const cur = addonByName.get(name) ?? { name, count: 0, revenueDollars: 0 };
        cur.count += 1;
        addonByName.set(name, cur);
      }
    }
    totalRevenue += priceDollars;
    if (isWalkin) walkinRevenue += priceDollars;
    else clientRevenue += priceDollars;
    chairTimeMin += minutes;
    cuts.push({
      visitId: v.id,
      timeLabel: time,
      clientId: v.client_id,
      clientName: null, // resolved by the modal via clientById lookup
      cutTypeName,
      basePrice: priceDollars,
      addonNames,
      totalPrice: priceDollars,
      actualMin: minutes,
      isWalkin,
    });
  }

  const cutCount = cuts.length;
  const avgTicket = cutCount > 0 ? Math.round(totalRevenue / cutCount) : 0;
  const attachRate = cutCount > 0 ? attachCount / cutCount : 0;

  const addonRollup = Array.from(addonByName.values()).sort(
    (a, b) => b.count - a.count || b.revenueDollars - a.revenueDollars,
  );

  return {
    cuts,
    totals: {
      cuts: cutCount,
      chairTimeMin,
      totalRevenue,
      walkinRevenue,
      clientRevenue,
      avgTicket,
      attachCount,
      attachRate,
    },
    addonRollup,
  };
}

function parseAddons(cutTypeName: string): string[] {
  // "Quick fade + Beard trim, Hot towel finish" → ["Beard trim", "Hot towel finish"]
  const plusIdx = cutTypeName.indexOf(' + ');
  if (plusIdx === -1) return [];
  const tail = cutTypeName.slice(plusIdx + 3).trim();
  if (!tail) return [];
  return tail.split(',').map((s) => s.trim()).filter(Boolean);
}

function formatChairTime(
  minutes: number,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (minutes < 60) return t('eod.minInChair', { m: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return t('eod.hourMinInChair', { h, m });
}

/* ============================================================================
 * Insight picker — weighted candidate list
 * ============================================================================ */

type InsightPick = { key: string; vars: Record<string, string | number> };

function pickInsight(agg: Aggregate, level: 'new' | 'mid' | 'master'): InsightPick {
  const candidates: Array<{ weight: number; pick: InsightPick }> = [];
  const totals = agg.totals;

  // Add-on attach signals
  if (totals.cuts > 0 && totals.attachRate >= 0.5) {
    candidates.push({
      weight: 8,
      pick: { key: 'eod.insight.attachStrong', vars: { pct: Math.round(totals.attachRate * 100) } },
    });
  } else if (totals.cuts >= 4 && totals.attachRate < 0.25) {
    candidates.push({
      weight: 6,
      pick: { key: 'eod.insight.attachWeak', vars: { pct: Math.round(totals.attachRate * 100) } },
    });
  }

  // Walk-in capture
  const walkinCount = agg.cuts.filter((c) => c.isWalkin).length;
  if (walkinCount >= 1) {
    candidates.push({
      weight: 7,
      pick: {
        key: walkinCount === 1 ? 'eod.insight.walkin_one' : 'eod.insight.walkin_other',
        vars: { count: walkinCount, rev: totals.walkinRevenue },
      },
    });
  }

  // Strong revenue day for this barber
  if (totals.totalRevenue >= 200) {
    candidates.push({
      weight: 6,
      pick: {
        key: totals.cuts === 1 ? 'eod.insight.strongRev_one' : 'eod.insight.strongRev_other',
        vars: { rev: totals.totalRevenue, cuts: totals.cuts },
      },
    });
  }

  // Experience-tiered fallbacks
  if (level === 'new' && totals.cuts >= 3) {
    candidates.push({
      weight: 5,
      pick: { key: 'eod.insight.newReps', vars: { cuts: totals.cuts } },
    });
  }
  if (level === 'master' && totals.cuts > 0) {
    candidates.push({
      weight: 5,
      pick: {
        key: 'eod.insight.masterAvg',
        vars: { avg: totals.avgTicket, bump: totals.cuts * 5 },
      },
    });
  }

  // Generic fallback
  if (candidates.length === 0) {
    if (level === 'new') {
      candidates.push({
        weight: 1,
        pick: {
          key: totals.cuts === 1 ? 'eod.insight.fallbackNew_one' : 'eod.insight.fallbackNew_other',
          vars: { cuts: totals.cuts },
        },
      });
    } else {
      candidates.push({
        weight: 1,
        pick: {
          key: totals.cuts === 1 ? 'eod.insight.fallback_one' : 'eod.insight.fallback_other',
          vars: { cuts: totals.cuts, rev: totals.totalRevenue },
        },
      });
    }
  }

  candidates.sort((a, b) => b.weight - a.weight);
  return candidates[0].pick;
}

/* ================ icons ================ */
function BarChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  );
}
function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
